import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

// Types
export interface TabItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  component: React.ReactNode;
  visible?: boolean;
}

interface ProfileTabsProps {
  tabs: TabItem[];
  defaultTab?: string;
}

// Styled Components
const Container = styled.div`
  display: flex;
  gap: 2rem;
  width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 200px;
  
  @media (max-width: 768px) {
    flex-direction: row;
    overflow-x: auto;
    overflow-y: hidden;
    min-width: 100%;
    gap: 0;
    border-bottom: 2px solid ${({ theme }) => theme.colors.border};
    margin-bottom: 1.5rem;
    
    /* Masquer la scrollbar mais garder le scroll */
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const Tab = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border: none;
  border-radius: 8px;
  background-color: ${({ $isActive, theme }) => 
    $isActive ? theme.colors.primary : 'transparent'};
  color: ${({ $isActive, theme }) => 
    $isActive ? 'white' : theme.colors.text};
  font-size: 0.95rem;
  font-weight: ${({ $isActive }) => ($isActive ? '600' : '500')};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  white-space: nowrap;
  
  &:hover {
    background-color: ${({ $isActive, theme }) => 
      $isActive ? theme.colors.primaryDark : theme.colors.backgroundLight};
  }
  
  svg {
    font-size: 1.1rem;
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    border-radius: 0;
    padding: 1rem 1.25rem;
    justify-content: center;
    flex: 1;
    min-width: fit-content;
    background-color: transparent;
    color: ${({ $isActive, theme }) => 
      $isActive ? theme.colors.primary : theme.colors.textLight};
    border-bottom: 3px solid ${({ $isActive, theme }) => 
      $isActive ? theme.colors.primary : 'transparent'};
    
    &:hover {
      background-color: ${({ theme }) => theme.colors.backgroundLight};
      color: ${({ theme }) => theme.colors.primary};
    }
    
    span {
      display: none;
    }
    
    svg {
      font-size: 1.3rem;
    }
  }
`;

const Content = styled.div`
  flex: 1;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ProfileTabs: React.FC<ProfileTabsProps> = ({ tabs, defaultTab }) => {
  const visibleTabs = tabs.filter(tab => tab.visible !== false);
  const [activeTab, setActiveTab] = useState<string>(
    defaultTab || visibleTabs[0]?.id || ''
  );

  // Charger le dernier onglet visité depuis le localStorage
  useEffect(() => {
    const savedTab = localStorage.getItem('profile_active_tab');
    if (savedTab && visibleTabs.some(tab => tab.id === savedTab)) {
      setActiveTab(savedTab);
    }
  }, [visibleTabs]);

  // Sauvegarder l'onglet actif dans le localStorage
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    localStorage.setItem('profile_active_tab', tabId);
  };

  const activeTabContent = visibleTabs.find(tab => tab.id === activeTab)?.component;

  return (
    <Container>
      <Sidebar>
        {visibleTabs.map(tab => (
          <Tab
            key={tab.id}
            $isActive={activeTab === tab.id}
            onClick={() => handleTabChange(tab.id)}
            aria-label={tab.label}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </Tab>
        ))}
      </Sidebar>
      
      <Content>
        {activeTabContent}
      </Content>
    </Container>
  );
};

export default ProfileTabs;
